import { useEffect, useState } from "react";
import { useI18n } from "@/i18n";
import { Bell, BellOff, BellRing } from "lucide-react";
import { toast } from "sonner";

/**
 * Small header pill that toggles browser push notifications for AI entry-price alerts.
 * Permission is requested on click. Status persists via the Notification API itself.
 */
export default function AlertBell() {
  const { t } = useI18n();
  const [supported, setSupported] = useState(false);
  const [perm, setPerm] = useState("default");

  useEffect(() => {
    if (typeof window === "undefined") return;
    const ok = "Notification" in window;
    setSupported(ok);
    if (ok) setPerm(Notification.permission);
  }, []);

  const onClick = async () => {
    if (!supported) {
      toast.error(t("alerts_unsupported"));
      return;
    }
    if (perm === "granted") {
      toast.success(t("alerts_on"));
      return;
    }
    if (perm === "denied") {
      toast.error(t("alerts_denied"));
      return;
    }
    try {
      const p = await Notification.requestPermission();
      setPerm(p);
      if (p === "granted") {
        toast.success(t("alerts_on"));
        new Notification("CRYPTO.AI", { body: t("alerts_enabled_body"), icon: "/logo192.png" });
      }
    } catch (e) {
      toast.error(t("alerts_error"));
    }
  };

  if (!supported) return null;
  const Icon = perm === "granted" ? BellRing : perm === "denied" ? BellOff : Bell;
  const cls = perm === "granted"
    ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
    : perm === "denied"
      ? "border-red-500/40 bg-red-500/10 text-red-300"
      : "border-border/60 bg-white/[0.02] text-zinc-400 hover:text-white";

  return (
    <button
      data-testid="alert-bell-btn"
      onClick={onClick}
      title={t("entry_alerts")}
      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-sm border text-[10px] font-mono uppercase tracking-widest transition-colors ${cls}`}
    >
      <Icon className="w-3 h-3" strokeWidth={1.7} />
      {t("entry_alerts")}
    </button>
  );
}
